import { useEffect, useState } from 'react';
import { Send, Play, Code2, Eye, Terminal, Zap, ChevronRight } from 'lucide-react';

/**
 * Cinematic "live builder" dashboard preview. Fake but realistic — animated
 * chat, streaming code, live app preview. This is the shiny centerpiece.
 */

const CHAT_LINES = [
  { role: 'user', text: 'Build a Stripe-powered marketplace for indie musicians.' },
  { role: 'ai', text: 'Provisioning MongoDB. Wiring Stripe. Drafting onboarding…' },
  { role: 'ai', text: 'Blueprint ready. Generating React + FastAPI code.' },
  { role: 'user', text: 'Add fan-club subscriptions.' },
  { role: 'ai', text: 'Subscription tiers added. Deploying to edge…' },
];

const CODE_LINES = [
  { c: 'import { Stripe }', a: ' from "@atw/payments";' },
  { c: 'const stripe =', a: ' new Stripe(process.env.STRIPE_KEY);' },
  { c: '', a: '' },
  { c: 'export async function', a: ' createArtistCheckout(artistId) {' },
  { c: '  const session =', a: ' await stripe.checkout.sessions.create({' },
  { c: '    mode:', a: ' "payment",' },
  { c: '    payment_method_types:', a: ' ["card"],' },
  { c: '    line_items:', a: ' await getArtistItems(artistId),' },
  { c: '    success_url:', a: ' `${SITE}/success`,' },
  { c: '  });', a: '' },
  { c: '  return', a: ' session.url;' },
  { c: '}', a: '' },
];

export default function LiveDashboardPreview() {
  const [chatIdx, setChatIdx] = useState(0);
  const [codeIdx, setCodeIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setChatIdx((i) => (i + 1) % (CHAT_LINES.length + 3));
    }, 2200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCodeIdx((i) => (i + 1) % (CODE_LINES.length + 4));
    }, 600);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="live-dashboard-preview"
      className="relative w-full max-w-6xl mx-auto rounded-[24px] border border-white/12 overflow-hidden"
      style={{
        boxShadow:
          '0 80px 160px -40px rgba(99,102,241,0.5), 0 40px 80px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)',
        background:
          'linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(2,6,23,0.98) 100%)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Traffic lights + toolbar */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/8 bg-black/30">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-rose-500/90 shadow-[0_0_10px_rgba(244,63,94,0.7)]" />
          <span className="h-3 w-3 rounded-full bg-amber-400/90 shadow-[0_0_10px_rgba(251,191,36,0.7)]" />
          <span className="h-3 w-3 rounded-full bg-emerald-500/90 shadow-[0_0_10px_rgba(16,185,129,0.7)]" />
        </div>
        <div className="ml-3 flex items-center gap-2 text-[11px] text-white/60">
          <span className="px-2 py-0.5 rounded bg-white/5 border border-white/8 tracking-wide">
            aroundtheworld.ai / build / marketplace
          </span>
          <ChevronRight size={12} />
          <span className="text-cyan-300 font-semibold uppercase tracking-widest text-[10px]">Live Session</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-300">
            Streaming
          </span>
        </div>
      </div>

      {/* 3-column split */}
      <div className="grid grid-cols-12">
        {/* Chat */}
        <div className="col-span-4 border-r border-white/8 min-h-[440px] flex flex-col">
          <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-white/70">
            <Zap size={12} className="text-cyan-300" /> AI Conversation
          </div>
          <div className="flex-1 p-4 space-y-3 overflow-hidden">
            {CHAT_LINES.slice(0, Math.min(chatIdx + 1, CHAT_LINES.length)).map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} slide-up-in`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-[12.5px] leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-gradient-to-br from-cyan-500/25 to-indigo-500/20 border border-cyan-400/25 text-white'
                      : 'bg-white/5 border border-white/8 text-slate-200'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {chatIdx < CHAT_LINES.length && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-1">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-white/8 flex items-center gap-2 bg-black/20">
            <div className="flex-1 h-8 rounded-lg bg-white/5 border border-white/10 px-3 flex items-center text-[12px] text-white/45">
              Ask anything to build…
            </div>
            <button className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center text-white shadow-[0_6px_16px_-6px_rgba(99,102,241,0.6)]">
              <Send size={13} />
            </button>
          </div>
        </div>

        {/* Code stream */}
        <div className="col-span-4 border-r border-white/8 min-h-[440px] flex flex-col bg-[#020617]/70">
          <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-white/70">
            <Code2 size={12} className="text-fuchsia-300" /> Streaming Code
            <span className="ml-auto text-[10px] font-normal text-white/40">payments.ts</span>
          </div>
          <div className="flex-1 p-4 font-mono text-[11.5px] leading-[1.7]">
            {CODE_LINES.slice(0, Math.min(codeIdx, CODE_LINES.length)).map((ln, i) => (
              <div key={i} className="flex slide-up-in" style={{ animationDelay: `${i * 30}ms` }}>
                <span className="w-6 text-white/25 select-none">{i + 1}</span>
                <span className="text-cyan-300">{ln.c}</span>
                <span className="text-fuchsia-300">{ln.a}</span>
              </div>
            ))}
            {codeIdx < CODE_LINES.length && (
              <div className="flex">
                <span className="w-6 text-white/25">{Math.min(codeIdx + 1, CODE_LINES.length)}</span>
                <span className="cursor-blink text-cyan-300">|</span>
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-white/8 flex items-center gap-2 text-[10px] text-white/50 bg-black/30 font-mono">
            <Terminal size={11} /> build ok • 0 errors • 12 files staged
          </div>
        </div>

        {/* Preview */}
        <div className="col-span-4 min-h-[440px] flex flex-col">
          <div className="px-4 py-2.5 border-b border-white/8 flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-white/70">
            <Eye size={12} className="text-emerald-300" /> Live Preview
            <span className="ml-auto text-[10px] font-normal text-white/40">mobile</span>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <PhonePreview />
          </div>
          <div className="px-4 py-3 border-t border-white/8 flex items-center justify-between text-[10px] text-white/60 bg-black/20">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span>Deployed to edge</span>
            </div>
            <button className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 hover:text-emerald-200">
              Open ↗
            </button>
          </div>
        </div>
      </div>

      {/* Bottom stats strip */}
      <div className="grid grid-cols-4 border-t border-white/8 bg-black/40 text-white">
        {[
          { label: 'Build time', val: '3m 42s', c: 'text-cyan-300' },
          { label: 'Files generated', val: '47', c: 'text-fuchsia-300' },
          { label: 'Tests passing', val: '128/128', c: 'text-emerald-300' },
          { label: 'Est. cost saved', val: '$18,400', c: 'text-amber-300' },
        ].map((s) => (
          <div key={s.label} className="px-4 py-3 border-r border-white/8 last:border-r-0">
            <div className={`text-[13px] font-black ${s.c}`}>{s.val}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhonePreview() {
  return (
    <div className="relative w-[190px] aspect-[9/19] rounded-[28px] border-[3px] border-white/15 bg-slate-950 overflow-hidden shadow-[0_25px_60px_-20px_rgba(99,102,241,0.5)]">
      {/* Notch */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 h-3 w-16 rounded-full bg-black" />
      {/* Screen content */}
      <div className="pt-7 px-3 h-full flex flex-col">
        <div className="text-[8px] text-white/50 font-bold tracking-widest">TONE.FM</div>
        <div className="text-[11px] font-black text-white leading-tight mt-1">
          Discover indie
          <br />
          artists tonight.
        </div>
        <div
          className="mt-3 h-24 rounded-lg"
          style={{
            background:
              'linear-gradient(135deg, #22d3ee, #6366f1 45%, #c026d3)',
          }}
        />
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-square rounded-md"
              style={{
                background: [
                  'linear-gradient(135deg,#f472b6,#fb7185)',
                  'linear-gradient(135deg,#38bdf8,#818cf8)',
                  'linear-gradient(135deg,#34d399,#22d3ee)',
                  'linear-gradient(135deg,#f59e0b,#f43f5e)',
                ][i],
              }}
            />
          ))}
        </div>
        <div className="mt-auto mb-2">
          <div className="h-8 rounded-lg bg-white text-slate-900 flex items-center justify-center gap-1 text-[9px] font-black tracking-wide">
            <Play size={9} fill="currentColor" /> LISTEN LIVE
          </div>
        </div>
      </div>
    </div>
  );
}
